// The 9 status keys form one continuous pipeline split across two HubSpot
// deals: 1-4 live on the tracking ("case") deal, 5-9 on the customer
// satisfaction deal. Keys are stable across environments per the API spec
// — not a HubSpot-internal numeric id.
export const trackingStatusOptions = [
  { value: "NEW_CENTER_CASE_RECEIVED", label: "New Center Case Received" },
  { value: "CASE_VERIFICATION", label: "Case Verification" },
  { value: "CASE_ASSIGNED_IN_PROGRESS", label: "Case Assigned & In Progress" },
  { value: "REPORT_SIGNED", label: "Report Signed" },
]

export const csatStatusOptions = [
  {
    value: "RECEIPT_CONFIRMATION_PENDING",
    label: "Receipt Confirmation Pending",
  },
  { value: "IMAGING_CENTER_CONFIRMED", label: "Imaging Center Confirmed" },
  { value: "REFERRING_DOCTOR_CONFIRMED", label: "Referring Doctor Confirmed" },
  { value: "CEO_OUTREACH_PENDING", label: "CEO Outreach Pending" },
  { value: "CEO_OUTREACH_COMPLETE", label: "CEO Outreach Complete" },
]

export const statusMeta = {
  NEW_CENTER_CASE_RECEIVED: {
    label: "New Center Case Received",
    tone: "neutral",
  },
  CASE_VERIFICATION: { label: "Case Verification", tone: "blue" },
  CASE_ASSIGNED_IN_PROGRESS: {
    label: "Case Assigned & In Progress",
    tone: "blue",
  },
  REPORT_SIGNED: { label: "Report Signed", tone: "indigo" },
  RECEIPT_CONFIRMATION_PENDING: {
    label: "Receipt Confirmation Pending",
    tone: "amber",
  },
  IMAGING_CENTER_CONFIRMED: {
    label: "Imaging Center Confirmed",
    tone: "green",
  },
  REFERRING_DOCTOR_CONFIRMED: {
    label: "Referring Doctor Confirmed",
    tone: "green",
  },
  CEO_OUTREACH_PENDING: { label: "CEO Outreach Pending", tone: "amber" },
  CEO_OUTREACH_COMPLETE: { label: "CEO Outreach Complete", tone: "emerald" },
}

// No "error"/red tone — this pipeline has no failure status, unlike e.g.
// RadPeerReports' ACR submission statuses.
export const toneStyles = {
  neutral: "bg-gray-100 border border-gray-300 text-gray-600",
  blue: "bg-sky-50 border border-sky-200 text-secondary_dark",
  indigo: "bg-indigo-50 border border-indigo-200 text-indigo-700",
  amber: "bg-amber-50 border border-amber-300 text-amber-800",
  green: "bg-green-50 border border-green-200 text-green-700",
  emerald: "bg-emerald-100 border border-emerald-300 text-emerald-800",
  teal: "bg-teal-50 border border-teal-200 text-teal-700",
}

export const toneDot = {
  neutral: "bg-gray-400",
  blue: "bg-secondary_dark",
  indigo: "bg-indigo-500",
  amber: "bg-amber-500",
  green: "bg-green-500",
  emerald: "bg-emerald-600",
  teal: "bg-teal-500",
}

// The 9 statuses as one ordered, mutually-exclusive pipeline (n: 1-9),
// regardless of which of the two deals a status lives on.
export const unifiedStageOptions = [
  ...trackingStatusOptions.map((o, i) => ({ ...o, n: i + 1 })),
  ...csatStatusOptions.map((o, i) => ({ ...o, n: i + 5 })),
]

// A case's current stage: the satisfaction deal's status if one exists
// (it's always further along than the tracking deal once created), else
// the tracking deal's status, else null ("no deal raised"). Both
// fields are already on every case row — this is pure derivation, no
// backend field needed for it.
export const getUnifiedStage = (study) => {
  if (study.CustomerSatisfactionStatus) {
    return (
      unifiedStageOptions.find(
        (s) => s.value === study.CustomerSatisfactionStatus
      )?.n ?? null
    )
  }
  if (study.TrackingDealStatus) {
    return (
      unifiedStageOptions.find((s) => s.value === study.TrackingDealStatus)
        ?.n ?? null
    )
  }
  return null
}

// Collapses the 9-stage pipeline into 4 buckets for a center's stage-mix
// mini bar — a coarser view than the per-status pill, deliberately using
// its own small palette rather than the 9 status tones.
export const STAGE_BUCKETS = [
  {
    key: "proc",
    label: "In Progress",
    tone: "blue",
    test: (n) => n >= 1 && n <= 3,
  },
  {
    key: "conf",
    label: "Receipt Confirmation Pending",
    tone: "teal",
    test: (n) => n >= 4 && n <= 7,
  },
  {
    key: "ceo",
    label: "CEO outreach",
    tone: "amber",
    test: (n) => n === 8,
  },
  { key: "done", label: "Completed", tone: "emerald", test: (n) => n === 9 },
]

export const getStageBucket = (n) => STAGE_BUCKETS.find((b) => b.test(n))

// In Progress covers two situations — still receiving cases, or all
// received but some still open — so a row's hint names which one applies.
// Without a center (the filter tab) it describes both.
const describeInProgress = ({ studyLimit, center }) => {
  if (!center || !Number.isFinite(studyLimit)) {
    return "Still receiving or working through tracked studies."
  }

  const received = center.TrackedStudyCount ?? center.CaseCount
  if (received < studyLimit) {
    return `Received ${received} of ${studyLimit} tracked studies so far.`
  }

  const open = center.CaseCount - center.CompleteCount
  return Number.isFinite(open)
    ? `All ${studyLimit} studies received; ${open} still open.`
    : `All ${studyLimit} studies received; some still open.`
}

// Center-level status bucket, computed server-side by the /centers
// endpoint's `Status` field (centerBucketFor in the backend). Completed
// means all tracked cases received AND every one at CEO Outreach Complete.
export const centerStatusOptions = [
  {
    value: "IN_PROGRESS",
    label: "In Progress",
    tone: "blue",
    describe: describeInProgress,
  },
  {
    value: "CEO_DUE",
    label: "CEO Call Due",
    tone: "amber",
    describe: () => "A study needs a CEO outreach call.",
  },
  {
    value: "COMPLETE",
    label: "Completed",
    tone: "emerald",
    describe: ({ studyLimit }) =>
      Number.isFinite(studyLimit)
        ? `All ${studyLimit} tracked studies received and closed out.`
        : "All tracked studies received and closed out.",
  },
]
